import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { PhoneNumberWithPrefix } from './phone-number-with-prefix';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControlComponent,
  PrimengInputTextDirective,
  SelectItem,
} from '@web/shared/ui';
import { isEmptyString, isNil, Nillable, valueOrDefault } from '@web/utils';
import { Select } from 'primeng/select';
import { map, Observable, of, Subject, switchMap } from 'rxjs';
import { DictionaryPhoneCountryPrefixSelectItemsProviderService } from '../../providers/dictionary-phone-country-prefix-select-items-provider.service';
import { InputText } from 'primeng/inputtext';
import { InputMask } from 'primeng/inputmask';

type Controls = {
  prefix: FormControl<Nillable<string>>;
  number: FormControl<Nillable<string>>;
};

type Form = FormGroup<Controls>;

@Component({
  selector: 'app-phone-number-with-prefix',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    Select,
    InputText,
    InputMask,
    PrimengInputTextDirective,
    FormControlComponent,
  ],
  templateUrl: './phone-number-with-prefix.component.html',
  styleUrl: './phone-number-with-prefix.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberWithPrefixComponent),
      multi: true,
    },
  ],
})
export class PhoneNumberWithPrefixComponent
  implements ControlValueAccessor, Validators, OnInit
{
  public readonly prefixesSelectItems$: Observable<SelectItem<string>[]>;

  protected readonly form: Form;
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly isNil = isNil;
  protected readonly numberMask = this.numberMaskSignal();

  /* prettier-ignore */
  private readonly selectItemsProvider = inject(DictionaryPhoneCountryPrefixSelectItemsProviderService);
  private readonly builder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly polishNumberPrefix = '+48';
  private readonly currentPrefix = signal<string>(this.polishNumberPrefix);
  /* prettier-ignore */
  private readonly writeValueSource$ = new Subject<Nillable<PhoneNumberWithPrefix>>();

  private onChange!: (value: Nillable<PhoneNumberWithPrefix>) => void;
  private onTouched!: () => void;

  public constructor() {
    this.form = this.buildForm();
    this.prefixesSelectItems$ = this.prefixesSelectItemsSource();
  }

  public ngOnInit(): void {
    this.listenToFormValueChanges();
    this.listenToWriteValue();
    this.listenToPrefixValueChanges();
  }

  public writeValue(numberWithPrefix: Nillable<PhoneNumberWithPrefix>): void {
    this.writeValueSource$.next(numberWithPrefix);
  }

  public registerOnChange(
    fn: (value: Nillable<PhoneNumberWithPrefix>) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public validate(control: FormControl): ValidationErrors | null {
    return this.form.valid ? null : this.form.errors;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private listenToWriteValue(): void {
    this.writeValueSource$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((numberWithPrefix) => {
          if (isNil(numberWithPrefix)) {
            return this.selectItemsProvider.fetchDefaultSelectItem().pipe(
              map((defaultSelectItem) => ({
                defaultPrefix: defaultSelectItem.value,
                originalValue: numberWithPrefix,
              })),
            );
          }

          return of({
            defaultPrefix: numberWithPrefix?.prefix,
            originalValue: numberWithPrefix,
          });
        }),
      )
      .subscribe({
        next: ({ defaultPrefix, originalValue }) => {
          this.currentPrefix.set(defaultPrefix);

          this.form.patchValue(
            {
              number: originalValue?.number,
              prefix: defaultPrefix,
            },
            { emitEvent: false },
          );
        },
      });
  }

  private buildForm(): Form {
    return this.builder.group({
      prefix: this.builder.control<Nillable<string>>(this.polishNumberPrefix),
      number: this.builder.control<Nillable<string>>(null),
    });
  }

  private listenToFormValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (numberWithPrefix) => {
        const { number, prefix } = numberWithPrefix;

        if (isNil(number) || isNil(prefix) || isEmptyString(number)) {
          this.onChange(null);

          return;
        }

        const numberWithoutSeparator = number.replace(/-/g, '');

        this.onChange(
          new PhoneNumberWithPrefix({
            prefix,
            number: numberWithoutSeparator,
          }),
        );
      },
    });
  }

  private prefixesSelectItemsSource(): Observable<SelectItem<string>[]> {
    return this.selectItemsProvider.selectItems();
  }

  private numberMaskSignal(): Signal<Nillable<string>> {
    return computed(() =>
      this.currentPrefix() === this.polishNumberPrefix ? '999-999-999' : null,
    );
  }

  private listenToPrefixValueChanges(): void {
    this.form.controls.prefix.valueChanges.subscribe((prefix) => {
      this.currentPrefix.set(valueOrDefault(prefix, this.polishNumberPrefix));
      this.form.controls.number.reset(null, { emitEvent: false });
    });
  }
}
